"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateSMSFragments = void 0;
const messaging_constants_1 = require("./messaging.constants");
/**
 * Large SMS messages are broken into "fragments". When only plain GSM (Global
 * System for Mobile Communications) characters are used each fragment can
 * contain up to 160 characters. If the message is small enough to fit into a
 * single fragment it can take up the entire fragment. If the message is larger
 * than a single fragment, each fragment needs a message header so the usable
 * space in the fragment drops to 153.
 *
 * @param charactersInSmsBody
 * @returns the usable size of each SMS fragment
 */
const gsmFragmentSize = (charactersInSmsBody) => charactersInSmsBody > 160 ? 153 : 160;
/**
 * Large SMS messages are broken into "fragments". When unicode or non-GSM
 * characters are used each fragment can contain up to 70 characters. If the
 * message is small enough to fit into a single fragment it can take up the
 * entire fragment. If the message is larger than a single fragment, each
 * fragment needs a message header so the usable space in the fragment drops to
 * 66.
 *
 * @param charactersInSmsBody
 * @returns the usable size of each SMS fragment
 */
const unicodeFragmentSize = (charactersInSmsBody) => charactersInSmsBody > 70 ? 66 : 70;
/**
 * calculates the number of fragments for a given text. It looks at the
 * characters contained in the text and then calls the appropriate method for
 * counting the fragments. On the backend, we replace some unicode characters
 * with an established GSM replacement as a cost-saving strategy for our users.
 * This means that the actual number of characters and fragments sent might be
 * different but this is considered fine for now as we are potentially sending
 * less than we might say on the client. This replacement is handled by the
 * GsmSanitisedMessage class
 * https://github.com/Accurx/rosemary/blob/trunk/src/Shared/accuRx.Shared/Classes/SmsCharacterResult.cs#L125
 *
 * @param smsBody The body of the message for which we want to calculate fragments
 * @returns number of fragments that will be sent
 */
const calculateSMSFragments = (smsBody) => {
    /**
     * SMS messages that contain any unicode characters are sent using a whole
     * different character set and cannot contain as many characters as plain
     * text (GSM) messages.
     */
    let unicode = false;
    /**
     * Keep a count of the amount of characters in the message. Our server
     * transforms some unicode characters into multiple GSM characters so the
     * final amount of characters may not be equal to smsBody.length.
     */
    let charCount = 0;
    /**
     * Some GSM characters take up the space of two characters. We keep a count
     * of them here because they only count as double for non-unicode messages
     * and we don't know yet if it's a unicode message so we'll add these on at
     * the end.
     */
    let doubleCharsCount = 0;
    for (let i = 0; i < smsBody.length; i++) {
        const char = smsBody[i];
        const isGSM = messaging_constants_1.GSM_CHARSET.includes(char);
        const isGSMDouble = messaging_constants_1.GSM_DOUBLE_CHARACTERS.includes(char);
        if (!isGSM &&
            !isGSMDouble &&
            messaging_constants_1.SANITISABLE_UNICODE_CHARACTERS[char] === undefined) {
            unicode = true;
        }
        if (messaging_constants_1.SANITISABLE_UNICODE_CHARACTERS[char] === undefined) {
            charCount++;
        }
        else {
            charCount = charCount + messaging_constants_1.SANITISABLE_UNICODE_CHARACTERS[char];
        }
        if (isGSMDouble) {
            doubleCharsCount++;
        }
    }
    /**
     * If it's a plain text (GSM) message then we need to account for any double
     * characters in the overall message size. If there are any unicode
     * characters a different character set is used and so double characters are
     * not a thing.
     */
    const smsCharacterCount = unicode
        ? charCount
        : charCount + doubleCharsCount;
    const fragmentSize = unicode
        ? unicodeFragmentSize(smsCharacterCount)
        : gsmFragmentSize(smsCharacterCount);
    const fragmentCount = Math.max(Math.ceil(smsCharacterCount / fragmentSize), 1);
    return {
        unicode,
        fragmentCount,
        fragmentSize,
        smsCharacterCount,
    };
};
exports.calculateSMSFragments = calculateSMSFragments;
//# sourceMappingURL=calculateSMSFragments.js.map